import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import {
  Shield, Plus, Edit, Eye, EyeOff, Mail, UserPlus,
  ChevronDown, Check, X
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/admin';

export default function AdminTeamPage() {
  const { token, hasPermission, admin: currentAdmin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    roleId: 2
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch admins and roles in parallel
      const [adminsRes, rolesRes] = await Promise.all([
        axios.get(`${API}/admins`, { headers }).catch(err => {
          console.error('Admins fetch error:', err.response?.status, err.response?.data);
          throw err;
        }),
        axios.get(`${API}/roles`, { headers }).catch(err => {
          console.error('Roles fetch error:', err.response?.status, err.response?.data);
          throw err;
        }),
      ]);

      // Handle admins response - check multiple possible formats
      const adminsList = adminsRes.data.admins || adminsRes.data || [];
      setAdmins(Array.isArray(adminsList) ? adminsList : []);

      // Handle roles response - check multiple possible formats
      const rolesList = rolesRes.data.roles || rolesRes.data || [];
      setRoles(Array.isArray(rolesList) ? rolesList : []);

    } catch (err) {
      console.error('FetchData Error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        config: err.config?.url
      });
      
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load team data';
      setError(errorMsg);
      toast.error(errorMsg);
      
      setAdmins([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.firstName) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/admins`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Admin created successfully');
      setShowCreateModal(false);
      setFormData({ email: '', password: '', firstName: '', lastName: '', roleId: 2 });
      fetchData();
    } catch (err) {
      console.error('Create admin error:', err);
      toast.error(err.response?.data?.error || 'Failed to create admin');
    }
  };

  const toggleAdminStatus = async (adminId, currentStatus) => {
    try {
      await axios.put(`${API}/admins/${adminId}`, {
        isActive: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Admin ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (err) {
      console.error('Toggle status error:', err);
      toast.error('Failed to update admin status');
    }
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'Super Admin': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'Admin': return 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30';
      case 'Moderator': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'Analyst': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-zinc-700 text-zinc-400 border-zinc-600';
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team-page">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Admin Team</h1>
          <p className="text-zinc-500 text-sm mt-1">Manage admin accounts and permissions</p>
          {error && (
            <p className="text-red-500 text-sm mt-2">⚠️ Error: {error}</p>
          )}
        </div>

        {hasPermission('admins.manage') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-500 text-white font-medium rounded-xl hover:bg-indigo-600 transition-all disabled:opacity-50"
            disabled={roles.length === 0}
          >
            <UserPlus className="w-4 h-4" />
            Add Admin
          </button>
        )}
      </div>

      {/* Roles overview */}
      {roles.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {roles.map((role) => (
            <div key={role.id} className="bg-zinc-900/50 rounded-2xl p-5 border border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(role.role_name)}`}>
                  {role.role_name}
                </span>
                <span className="text-zinc-500 text-sm">{role.admin_count || 0} members</span>
              </div>
              <p className="text-zinc-400 text-sm">{role.description}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <p className="text-yellow-600">No roles loaded. Check network tab for API errors.</p>
        </div>
      )}

      {/* Admins list */}
      <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
        <div className="p-6 border-b border-zinc-800">
          <h2 className="text-white font-semibold" style={{ fontFamily: 'Manrope' }}>Team Members</h2>
        </div>

        <div className="divide-y divide-zinc-800/50">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="p-6">
                <div className="h-16 bg-zinc-800 rounded animate-pulse" />
              </div>
            ))
          ) : admins.length > 0 ? (
            admins.map((admin) => (
              <div key={admin.id} className="p-6 hover:bg-zinc-800/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {admin.first_name?.charAt(0) || 'A'}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">
                          {admin.first_name} {admin.last_name}
                        </p>
                        {admin.id === currentAdmin?.id && (
                          <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-400 text-xs rounded">You</span>
                        )}
                      </div>
                      <p className="text-zinc-500 text-sm">{admin.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(admin.role_name)}`}>
                      {admin.role_name}
                    </span>

                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${admin.is_active ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                      <span className="text-zinc-500 text-sm">
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {hasPermission('admins.manage') && admin.id !== currentAdmin?.id && (
                      <button
                        onClick={() => toggleAdminStatus(admin.id, admin.is_active)}
                        className={`p-2 rounded-lg transition-all ${admin.is_active
                          ? 'text-red-400 hover:bg-red-500/20'
                          : 'text-emerald-400 hover:bg-emerald-500/20'
                          }`}
                        title={admin.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {admin.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>

                {admin.last_login && (
                  <p className="text-zinc-600 text-xs mt-2 ml-16">
                    Last login: {new Date(admin.last_login).toLocaleString()}
                  </p>
                )}
              </div>
            ))
          ) : (
            <div className="p-12 text-center text-zinc-500">
              No admin accounts found
            </div>
          )}
        </div>
      </div>

      {/* Create admin modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-md w-full border border-zinc-800">
            <div className="p-6 border-b border-zinc-800">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                Add New Admin
              </h3>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 pr-12 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Role *</label>
                <select
                  value={formData.roleId}
                  onChange={(e) => setFormData({ ...formData, roleId: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="">Select a role...</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>{role.role_name}</option>
                  ))}
                </select>
                {roles.length === 0 && (
                  <p className="text-red-500 text-xs mt-1">⚠️ No roles available. Check your connection.</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={roles.length === 0}
                  className="flex-1 py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}